import type { TimeRangePreset } from '@app/utils/time';
import { useEffect, useMemo, useRef, useState } from 'react';

const resolveIframeSrc = (relativeUrl: string): string => {
  if (typeof window === 'undefined') {
    return relativeUrl;
  }
  try {
    return new URL(relativeUrl, window.location.origin).toString();
  } catch {
    return relativeUrl;
  }
};

export interface GrafanaIframeWidget {
  id: string;
  title: string;
  iframeUrl?: string | null;
}

export interface GrafanaIframeCardProps {
  widget: GrafanaIframeWidget;
  height?: number;
  refreshSeconds?: number;
  timeRangePreset?: TimeRangePreset;
  onAuthRefresh?: (() => Promise<void> | void) | null;
}

interface GrafanaIframeViewportProps {
  widget: GrafanaIframeWidget;
  height: number | string;
  refreshSeconds: number;
  timeRangePreset?: TimeRangePreset;
  onAuthRefresh?: (() => Promise<void> | void) | null;
}

const presetToMs = (preset: TimeRangePreset): number => {
  switch (preset) {
    case '15m':
      return 15 * 60 * 1000;
    case '1h':
      return 60 * 60 * 1000;
    case '6h':
      return 6 * 60 * 60 * 1000;
    case '24h':
      return 24 * 60 * 60 * 1000;
    default:
      return 60 * 60 * 1000;
  }
};

const withReapRefreshToken = (absoluteUrl: string, token: string, timeRangePreset?: TimeRangePreset): string => {
  try {
    const u = new URL(absoluteUrl);
    u.searchParams.set('__reap_iframe_refresh', token);
    if (timeRangePreset) {
      const now = Date.now();
      const rangeMs = presetToMs(timeRangePreset);
      u.searchParams.set('from', String(now - rangeMs));
      u.searchParams.set('to', String(now));
    }
    return u.toString();
  } catch {
    // Fall back to a simple append; this is only used as a cache-buster.
    const sep = absoluteUrl.includes('?') ? '&' : '?';
    return `${absoluteUrl}${sep}__reap_iframe_refresh=${encodeURIComponent(token)}`;
  }
};

const GrafanaIframeViewport = ({
  widget,
  height,
  refreshSeconds,
  timeRangePreset,
  onAuthRefresh
}: GrafanaIframeViewportProps) => {
  const baseSrc = useMemo(() => (widget.iframeUrl ? resolveIframeSrc(widget.iframeUrl) : ''), [widget.iframeUrl]);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const refreshEnabled = refreshSeconds > 0 && isNearViewport;

  const [activeFrame, setActiveFrame] = useState<0 | 1>(0);
  const [frameSrc, setFrameSrc] = useState<[string, string]>(['', '']);

  const activeFrameRef = useRef<0 | 1>(0);
  const pendingSwapToRef = useRef<0 | 1 | null>(null);
  const frameRef0 = useRef<HTMLIFrameElement | null>(null);
  const frameRef1 = useRef<HTMLIFrameElement | null>(null);
  const authRefreshInFlightRef = useRef(false);
  const lastAuthRetryAtRef = useRef<number>(0);
  const authRetryCountRef = useRef<number>(0);
  const authCooldownUntilRef = useRef<number>(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsNearViewport(entry.isIntersecting),
      { rootMargin: '600px 0px' }
    );
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    activeFrameRef.current = activeFrame;
  }, [activeFrame]);

  useEffect(() => {
    if (!baseSrc || !isNearViewport) {
      setFrameSrc(['', '']);
      setActiveFrame(0);
      pendingSwapToRef.current = null;
      authRefreshInFlightRef.current = false;
      authRetryCountRef.current = 0;
      authCooldownUntilRef.current = 0;
      return;
    }

    // Reset: show a single iframe immediately using the stable base URL.
    setFrameSrc([baseSrc, '']);
    setActiveFrame(0);
    pendingSwapToRef.current = null;
    authRefreshInFlightRef.current = false;
    authRetryCountRef.current = 0;
    authCooldownUntilRef.current = 0;
  }, [baseSrc, isNearViewport]);

  const isAuthFailureFrame = (frame: HTMLIFrameElement | null): boolean => {
    if (!frame) {
      return false;
    }
    try {
      const href = frame.contentWindow?.location?.href;
      if (!href) {
        return false;
      }
      const u = new URL(href);
      const path = u.pathname.toLowerCase();
      // Common Grafana auth-related routes when hosted at /grafana.
      if (path.includes('/login') || path.includes('/logout') || path.includes('/signin') || path.includes('/auth')) {
        return true;
      }
      const title = frame.contentDocument?.title?.toLowerCase() ?? '';
      if (title.includes('sign in') || title.includes('login')) {
        return true;
      }
      return false;
    } catch {
      // If cross-origin or blocked, we can't introspect. Keepalive should cover most cases.
      return false;
    }
  };

  const refreshAuthAndRetry = async (idx: 0 | 1) => {
    if (!onAuthRefresh) {
      return;
    }
    if (authRefreshInFlightRef.current) {
      return;
    }

    const now = Date.now();
    if (now < authCooldownUntilRef.current) {
      return;
    }
    if (now - lastAuthRetryAtRef.current < 10_000) {
      authRetryCountRef.current += 1;
    } else {
      authRetryCountRef.current = 1;
    }
    lastAuthRetryAtRef.current = now;

    // Avoid infinite tight loops if something else is wrong (e.g. Grafana down / routing broken).
    if (authRetryCountRef.current > 3) {
      authCooldownUntilRef.current = now + 60_000;
      console.warn('[grafana-iframe] too many auth retries; cooling down for 60s');
      return;
    }

    authRefreshInFlightRef.current = true;
    try {
      await Promise.resolve(onAuthRefresh());
      // Re-load the same frame after the cookie refresh; keep the current swap intent.
      setFrameSrc((prev) => {
        const out: [string, string] = [...prev] as [string, string];
        out[idx] = withReapRefreshToken(baseSrc, String(Date.now()), timeRangePreset);
        return out;
      });
    } finally {
      authRefreshInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!baseSrc || !refreshEnabled) {
      return;
    }

    const tick = () => {
      const next: 0 | 1 = activeFrameRef.current === 0 ? 1 : 0;
      pendingSwapToRef.current = next;
      setFrameSrc((prev) => {
        const out: [string, string] = [...prev] as [string, string];
        // Cache-bust so the iframe actually reloads even if URL/time range is unchanged.
        out[next] = withReapRefreshToken(baseSrc, String(Date.now()), timeRangePreset);
        return out;
      });
    };

    // Trigger refresh on a fixed interval. The initial iframe remains stable; we swap only after the hidden
    // iframe has finished loading to avoid visible flicker.
    const id = window.setInterval(tick, refreshSeconds * 1000);
    return () => window.clearInterval(id);
  }, [baseSrc, refreshEnabled, refreshSeconds, timeRangePreset]);

  const handleFrameLoad = (idx: 0 | 1) => {
    const frame = idx === 0 ? frameRef0.current : frameRef1.current;
    if (isAuthFailureFrame(frame)) {
      // Don't swap to a login/unauthorized view; refresh the embed session and retry this iframe load.
      refreshAuthAndRetry(idx);
      return;
    }

    if (!refreshEnabled) {
      return;
    }
    if (pendingSwapToRef.current !== idx) {
      return;
    }
    pendingSwapToRef.current = null;
    // Swap on the next paint for a smoother cross-fade.
    window.requestAnimationFrame(() => setActiveFrame(idx));
  };

  return (
    <>
      {baseSrc ? (
        <div ref={viewportRef} className="grafana-iframe-card__frames" style={{ height }}>
          {frameSrc[0] ? (
            <iframe
              className={[
                'grafana-iframe-card__frame',
                activeFrame === 0 ? 'grafana-iframe-card__frame--active' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              src={frameSrc[0]}
              title={widget.title}
              loading="lazy"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
              ref={(node) => {
                frameRef0.current = node;
              }}
              onLoad={() => handleFrameLoad(0)}
            />
          ) : null}
          {refreshEnabled && frameSrc[1] ? (
            <iframe
              className={[
                'grafana-iframe-card__frame',
                activeFrame === 1 ? 'grafana-iframe-card__frame--active' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              src={frameSrc[1]}
              title={widget.title}
              loading="eager"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
              ref={(node) => {
                frameRef1.current = node;
              }}
              onLoad={() => handleFrameLoad(1)}
            />
          ) : null}
        </div>
      ) : (
        <p className="grafana-iframe-card__empty">No Grafana iframe URL provided for this widget.</p>
      )}
    </>
  );
};

const GrafanaIframeCard = ({ widget, height = 320, refreshSeconds = 0, timeRangePreset, onAuthRefresh }: GrafanaIframeCardProps) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [expanded]);

  return (
    <>
      <div className="grafana-iframe-card">
        <div className="grafana-iframe-card__header">
          <h3 className="grafana-iframe-card__title">{widget.title}</h3>
          {widget.iframeUrl ? (
            <button
              type="button"
              className="grafana-iframe-card__action"
              onClick={() => setExpanded(true)}
              aria-label={`Expand ${widget.title}`}
            >
              Expand
            </button>
          ) : null}
        </div>
        <GrafanaIframeViewport
          widget={widget}
          height={height}
          refreshSeconds={refreshSeconds}
          timeRangePreset={timeRangePreset}
          onAuthRefresh={onAuthRefresh}
        />
      </div>

      {expanded ? (
        <div className="grafana-iframe-modal" role="dialog" aria-modal="true" aria-label={widget.title}>
          <div className="grafana-iframe-modal__backdrop" onClick={() => setExpanded(false)} />
          <div className="grafana-iframe-modal__panel">
            <div className="grafana-iframe-modal__header">
              <h3 className="grafana-iframe-modal__title">{widget.title}</h3>
              <div className="grafana-iframe-modal__actions">
                {widget.iframeUrl ? (
                  <a
                    className="grafana-iframe-card__action"
                    href={resolveIframeSrc(widget.iframeUrl)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Grafana
                  </a>
                ) : null}
                <button type="button" className="grafana-iframe-card__action" onClick={() => setExpanded(false)}>
                  Close
                </button>
              </div>
            </div>
            <GrafanaIframeViewport
              widget={widget}
              height="calc(90vh - 72px)"
              refreshSeconds={refreshSeconds}
              timeRangePreset={timeRangePreset}
              onAuthRefresh={onAuthRefresh}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default GrafanaIframeCard;
